import {
  GetCloudName,
  GetProviderName,
  GPUInstanceType,
} from "types/InstanceTypes";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { OrgContext } from "contexts/OrgContext";
import { InstanceTypesContext } from "contexts/InstanceTypesContext";
import {
  getFilteredBasePriceAmount,
  isFreeTierInstance,
  useFreeTier,
  withServiceCharge,
} from "utils/freeTierUtils";
import {
  ChipType,
  preferenceList,
} from "../../ComputePicker/ChipToggleButtons";
import { convertToGPUMap, parseMemoryToGiB } from "../../utils/index";
import ComputeLoader from "../ComputeLoader";
import posthog from "posthog-js";

import NvidiaDGXDataCenter from "assets/img/png/dgxDatacenter.png";
import { useWindupString } from "windups";
import { Button } from "components/UI-lib";
import {
  displayPlatformType,
  dummyDGXInstanceObject,
} from "components/Environment/utils";
import DGXComputeCard from "./DGX/DGXComputeCard";
import { ContainerSelected } from "components/Environment/shared/BuildTypes";
import { useHistory, useLocation } from "react-router";
import { useIdentityFeatureFlagEnabled } from "utils/posthogFeatureFlags";
import RackCard from "../RackCard/index";
import CpuInstanceCard from "../CpuInstanceCard";
import { getPrimaryInstanceCloudCred } from "utils/cloudCreds";
import {
  CheckIcon,
  CpuChipIcon,
  FunnelIcon,
  ArrowPathIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import SortByDropdown from "./SortByDropdown";
import { gpuNamesMatch } from "utils/gpuNames";
import classNames from "classnames";
import { GPUVariant, groupInstancesByGPUVariant } from "./gpuVariants";

export interface AdvancedSelectorProps {
  allGPUInstances: GPUInstanceType[] | undefined;
  displayGPUInstances: GPUInstanceType[] | undefined;
  cpuInstances: GPUInstanceType[];
  setSelectedInstance(instanceObj: GPUInstanceType | undefined): void;
  containerSelected: ContainerSelected;
  selectedCloudName: string;
  isLoadingInstances: boolean;
  hideDgxc: boolean;
  persistentOnly: boolean;
  flexiblePortControlOnly: boolean;
  flexibleStorageOnly: boolean;
  animateLoaderCarouselLayout?: boolean;
  syncGpuSelectionToUrl?: boolean;
  initialSelectedChip?: string;
  pickerAllowedLocations?: Set<string> | null;
  selectedFamily: string | undefined;
  setSelectedFamily(family: string | undefined): void;
  selectedGPUVariant: SelectedGPUVariant | undefined;
  setSelectedGPUVariant(selection: SelectedGPUVariant | undefined): void;
}

export interface SelectedGPUVariant {
  gpuName: string;
  variantKey: string;
  gpuCount: number;
}
import queryString from "query-string";

const AdvancedSelector: React.FC<AdvancedSelectorProps> = ({
  allGPUInstances,
  displayGPUInstances,
  cpuInstances,
  setSelectedInstance,
  containerSelected,
  selectedCloudName,
  isLoadingInstances,
  hideDgxc,
  persistentOnly,
  flexiblePortControlOnly,
  flexibleStorageOnly,
  animateLoaderCarouselLayout,
  syncGpuSelectionToUrl = true,
  initialSelectedChip,
  pickerAllowedLocations,
  selectedFamily,
  setSelectedFamily,
  selectedGPUVariant,
  setSelectedGPUVariant,
}) => {
  const location = useLocation();
  const history = useHistory();
  const dgxcOnly = useIdentityFeatureFlagEnabled("dgxc-only");
  const queryParams = queryString.parse(location.search);

  // CPU instances for the current cloud: when GPU list has items, only show CPUs from same provider(s); when no GPUs (e.g. GPU-specific filters), show all CPUs
  const displayCPUInstances = useMemo(() => {
    if (displayGPUInstances === undefined) return [];
    return cpuInstances;
  }, [cpuInstances, displayGPUInstances]);

  const gpuList = useMemo(() => {
    if (displayGPUInstances === undefined) return undefined;
    const gpuInstances = dgxcOnly
      ? displayGPUInstances.filter(
          (instance) =>
            getPrimaryInstanceCloudCred(instance)?.id === "dgxc-gcp-1",
        )
      : displayGPUInstances;
    const _gpuMap = convertToGPUMap(gpuInstances);
    const gpuChips = Object.keys(_gpuMap).map((gpuName) => {
      return {
        name: gpuName,
        manufacturer: _gpuMap[gpuName][0].supported_gpus[0].manufacturer,
      };
    });
    // CPU first, then GPU chips (so divider after index 0 when CPU is present)
    if (displayCPUInstances.length > 0) {
      return [{ name: "CPU", manufacturer: "" }, ...gpuChips];
    }
    return gpuChips;
  }, [dgxcOnly, displayGPUInstances, displayCPUInstances]);

  const dgxAvailable = useMemo(
    () =>
      !hideDgxc &&
      (allGPUInstances ?? []).some(
        (instance) =>
          getPrimaryInstanceCloudCred(instance)?.id === "dgxc-gcp-1",
      ),
    [allGPUInstances, hideDgxc],
  );

  const setSelectedGpu = useCallback(
    (gpu: string) => {
      setSelectedFamily(gpu);
      if (
        selectedGPUVariant &&
        !gpuNamesMatch(selectedGPUVariant.gpuName, gpu)
      ) {
        setSelectedGPUVariant(undefined);
      }

      if (!syncGpuSelectionToUrl) {
        return;
      }

      // Parse query params fresh to avoid stale closure
      const currentParams = queryString.parse(location.search);
      const newUrl = `?${queryString.stringify({ ...currentParams, gpu })}`;
      history.replace(newUrl);
    },
    [
      history,
      location.search,
      selectedGPUVariant,
      setSelectedFamily,
      setSelectedGPUVariant,
      syncGpuSelectionToUrl,
    ],
  );

  const selectedGpu: string | undefined = useMemo(() => {
    const gpuQueryParam = syncGpuSelectionToUrl
      ? (queryParams.gpu ?? queryParams.chip ?? "")
      : "";
    const _gpu = (
      typeof gpuQueryParam === "string"
        ? gpuQueryParam
        : (gpuQueryParam?.[0] ?? "")
    )?.trim();

    if (syncGpuSelectionToUrl && _gpu) {
      if (_gpu.toLowerCase() === "dgx-h100") return "dgx-H100";
      if (_gpu.toLowerCase() === "cpu") return "CPU";

      const gpuFromQueryParam = gpuList?.find((gpu) =>
        gpuNamesMatch(gpu.name, _gpu),
      );
      if (gpuFromQueryParam) return gpuFromQueryParam.name;
    }

    if (selectedFamily) {
      if (selectedFamily.toLowerCase() === "dgx-h100") return "dgx-H100";
      if (selectedFamily.toLowerCase() === "cpu") return "CPU";

      const restoredFamily = gpuList?.find((gpu) =>
        gpuNamesMatch(gpu.name, selectedFamily),
      );
      if (restoredFamily) return restoredFamily.name;
    }

    if (selectedGPUVariant) {
      const restoredGpu = gpuList?.find((gpu) =>
        gpuNamesMatch(gpu.name, selectedGPUVariant.gpuName),
      );
      if (restoredGpu) return restoredGpu.name;
    }

    const defaultGpuName = dgxcOnly ? "dgx-H100" : "L40S";
    const defaultGpu = gpuList?.find(
      (g) => g.name.toLowerCase() === defaultGpuName.toLowerCase(),
    );
    return defaultGpu?.name ?? gpuList?.[0]?.name;
  }, [
    queryParams.chip,
    queryParams.gpu,
    dgxcOnly,
    gpuList,
    selectedFamily,
    selectedGPUVariant,
    syncGpuSelectionToUrl,
  ]);

  useEffect(() => {
    if (selectedGpu && selectedFamily !== selectedGpu) {
      setSelectedFamily(selectedGpu);
    }
  }, [selectedFamily, selectedGpu, setSelectedFamily]);

  useEffect(() => {
    if (isLoadingInstances || gpuList === undefined) return;

    if (
      selectedGPUVariant &&
      (!selectedGpu || !gpuNamesMatch(selectedGPUVariant.gpuName, selectedGpu))
    ) {
      setSelectedGPUVariant(undefined);
    }
  }, [
    gpuList,
    isLoadingInstances,
    selectedGPUVariant,
    selectedGpu,
    setSelectedGPUVariant,
  ]);

  if (isLoadingInstances) {
    return (
      <div className="py-20">
        <ComputeLoader animateCarouselLayout={animateLoaderCarouselLayout} />
      </div>
    );
  }

  if (displayGPUInstances === undefined) {
    return null; // Instances aren't loading but we haven't filtered the instances yet
  }

  // Only show empty state when neither GPU nor CPU options exist for current filters/cloud
  if (displayGPUInstances.length === 0 && displayCPUInstances.length === 0) {
    return (
      <EmptyState
        selectedGpuCount="All"
        selectedVram="All"
        onResetFilters={() => {}}
      />
    );
  }

  return (
    <div className="grid items-start gap-6 lg:grid-cols-[220px_minmax(0,1fr)]">
      <ComputeFamilyList
        families={gpuList ?? []}
        displayGPUInstances={displayGPUInstances}
        cpuInstanceCount={displayCPUInstances.length}
        selectedFamily={selectedGpu}
        onSelectFamily={setSelectedGpu}
        dgxAvailable={dgxAvailable}
        selectedCloudName={selectedCloudName}
      />

      <div className="min-w-0 flex flex-col">
        {selectedGpu !== undefined &&
          selectedGpu !== "dgx-H100" &&
          selectedGpu !== "CPU" && (
            <ComputeOptions
              key={selectedGpu}
              selectedGpu={selectedGpu}
              selectedVariantKey={
                selectedGPUVariant &&
                gpuNamesMatch(selectedGPUVariant.gpuName, selectedGpu)
                  ? selectedGPUVariant.variantKey
                  : undefined
              }
              selectedGpuCount={
                selectedGPUVariant &&
                gpuNamesMatch(selectedGPUVariant.gpuName, selectedGpu)
                  ? selectedGPUVariant.gpuCount
                  : undefined
              }
              setSelectedVariantKey={(variantKey, gpuCount) =>
                setSelectedGPUVariant(
                  variantKey !== undefined && gpuCount !== undefined
                    ? { gpuName: selectedGpu, variantKey, gpuCount }
                    : undefined,
                )
              }
              flexiblePortControlOnly={flexiblePortControlOnly}
              flexibleStorageOnly={flexibleStorageOnly}
              pickerAllowedLocations={pickerAllowedLocations}
              displayGPUInstances={displayGPUInstances
                .filter(
                  (instance) =>
                    instance.supported_gpus &&
                    instance.supported_gpus.length > 0 &&
                    gpuNamesMatch(instance.supported_gpus[0].name, selectedGpu),
                )
                .filter((instance) =>
                  !!containerSelected.vmBuild ||
                  !!containerSelected.customContainer
                    ? getPrimaryInstanceCloudCred(instance)?.platformType !==
                      "akash"
                    : true,
                )
                .sort((a, b) => {
                  return (
                    Number(
                      getFilteredBasePriceAmount(a, pickerAllowedLocations),
                    ) -
                    Number(
                      getFilteredBasePriceAmount(b, pickerAllowedLocations),
                    )
                  );
                })}
              setSelectedInstance={(instance) => {
                setSelectedInstance(instance);
                posthog.capture("Selected Instance", {
                  tab: "Advanced",
                  instanceType: instance?.type,
                  gpuName: instance?.supported_gpus
                    ? instance?.supported_gpus[0].name
                    : "",
                  cloudProvider:
                    getPrimaryInstanceCloudCred(instance)?.platformType,
                });
              }}
            />
          )}
        {selectedGpu === "dgx-H100" && (
          <DGXSetup
            setSelectedInstance={setSelectedInstance}
            pickerAllowedLocations={pickerAllowedLocations}
          />
        )}
        {selectedGpu === "CPU" && (
          <CPUOptions
            cpuInstances={displayCPUInstances}
            flexiblePortControlOnly={flexiblePortControlOnly}
            flexibleStorageOnly={flexibleStorageOnly}
            pickerAllowedLocations={pickerAllowedLocations}
            setSelectedInstance={(instance) => {
              setSelectedInstance(instance);
              posthog.capture("Selected Instance", {
                tab: "Advanced",
                instanceType: instance?.type,
                gpuName: "",
                cloudProvider:
                  getPrimaryInstanceCloudCred(instance)?.platformType,
              });
            }}
          />
        )}
      </div>
    </div>
  );
};

export default AdvancedSelector;

interface SectionHeadingProps {
  id: string;
  title: string;
}

const SectionHeading: React.FC<SectionHeadingProps> = ({ id, title }) => (
  <h2 id={id} tabIndex={-1} className="text-lg font-semibold text-white">
    {title}
  </h2>
);

interface ComputeFamilyListProps {
  families: ChipType[];
  displayGPUInstances: GPUInstanceType[];
  cpuInstanceCount: number;
  selectedFamily: string | undefined;
  onSelectFamily: (family: string) => void;
  dgxAvailable: boolean;
  selectedCloudName: string;
}

function ComputeFamilyList({
  families,
  displayGPUInstances,
  cpuInstanceCount,
  selectedFamily,
  onSelectFamily,
  dgxAvailable,
  selectedCloudName,
}: ComputeFamilyListProps) {
  const [familySearch, setFamilySearch] = useState("");
  const familyListRef = useRef<HTMLDivElement>(null);
  const sortedFamilies = useMemo(() => {
    return [...families].sort((a, b) => {
      if (a.name === "CPU") return -1;
      if (b.name === "CPU") return 1;

      const aIndex = preferenceList.findIndex(({ name }) => name === a.name);
      const bIndex = preferenceList.findIndex(({ name }) => name === b.name);
      if (aIndex === -1 && bIndex === -1) return a.name.localeCompare(b.name);
      if (aIndex === -1) return 1;
      if (bIndex === -1) return -1;
      return aIndex - bIndex;
    });
  }, [families]);

  const visibleFamilies =
    selectedCloudName === "dgxc-gcp-1" ? [] : sortedFamilies;
  const normalizedFamilySearch = familySearch.trim().toLowerCase();
  const filteredFamilies = visibleFamilies.filter((family) =>
    family.name.toLowerCase().includes(normalizedFamilySearch),
  );
  const showDgxFamily =
    dgxAvailable && "DGX Cloud".toLowerCase().includes(normalizedFamilySearch);

  const handleFamilyKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (
      ![
        "ArrowDown",
        "ArrowRight",
        "ArrowUp",
        "ArrowLeft",
        "Home",
        "End",
      ].includes(event.key)
    ) {
      return;
    }

    const familyButtons = Array.from(
      familyListRef.current?.querySelectorAll<HTMLButtonElement>(
        '[role="radio"]',
      ) ?? [],
    );
    const currentIndex = familyButtons.indexOf(
      event.target as HTMLButtonElement,
    );
    if (currentIndex === -1 || familyButtons.length === 0) return;

    event.preventDefault();
    const nextIndex =
      event.key === "Home"
        ? 0
        : event.key === "End"
          ? familyButtons.length - 1
          : event.key === "ArrowDown" || event.key === "ArrowRight"
            ? (currentIndex + 1) % familyButtons.length
            : (currentIndex - 1 + familyButtons.length) % familyButtons.length;
    const nextButton = familyButtons[nextIndex];
    nextButton.focus();
    nextButton.click();
  };

  const getFamilyCounts = (familyName: string) => {
    if (familyName === "CPU") {
      return { variants: 0, skus: cpuInstanceCount };
    }

    const instances = displayGPUInstances.filter(
      (instance) =>
        instance.supported_gpus?.[0] &&
        gpuNamesMatch(instance.supported_gpus[0].name, familyName),
    );
    return {
      variants: groupInstancesByGPUVariant(instances).length,
      skus: instances.length,
    };
  };

  return (
    <aside aria-labelledby="compute-family-heading" className="min-w-0">
      <SectionHeading id="compute-family-heading" title="Family" />
      <label className="relative mt-4 block">
        <span className="sr-only">Search compute families</span>
        <MagnifyingGlassIcon
          aria-hidden="true"
          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500"
        />
        <input
          type="search"
          value={familySearch}
          onChange={(event) => setFamilySearch(event.target.value)}
          placeholder="Search families"
          className="w-full rounded-md border border-zinc-700 bg-zinc-900 py-2 pl-9 pr-3 text-sm text-white outline-none placeholder:text-zinc-500 focus:border-highlight focus:ring-1 focus:ring-highlight"
        />
      </label>
      <div
        ref={familyListRef}
        onKeyDown={handleFamilyKeyDown}
        className="mt-3 flex max-h-[420px] flex-wrap gap-2 overflow-y-auto pr-2 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-zinc-700 lg:max-h-[570px] lg:flex-col lg:flex-nowrap"
        role="radiogroup"
        aria-label="Compute families"
      >
        {showDgxFamily && (
          <ComputeFamilyButton
            name="DGX Cloud"
            description="Managed H100"
            selected={selectedFamily === "dgx-H100"}
            onSelect={() => onSelectFamily("dgx-H100")}
          />
        )}
        {filteredFamilies.map((family) => {
          const { variants, skus } = getFamilyCounts(family.name);
          const isCPU = family.name === "CPU";
          const description = isCPU
            ? `${skus} ${skus === 1 ? "option" : "options"}`
            : `${variants} ${variants === 1 ? "variant" : "variants"} · ${skus} ${skus === 1 ? "option" : "options"}`;

          return (
            <ComputeFamilyButton
              key={family.name}
              name={family.name}
              description={description}
              selected={selectedFamily === family.name}
              onSelect={() => onSelectFamily(family.name)}
              isCPU={isCPU}
            />
          );
        })}
        {!showDgxFamily && filteredFamilies.length === 0 && (
          <p className="w-full px-2 py-4 text-sm text-secondary">
            No matching families
          </p>
        )}
      </div>
    </aside>
  );
}

interface ComputeFamilyButtonProps {
  name: string;
  description: string;
  selected: boolean;
  onSelect: () => void;
  isCPU?: boolean;
}

function ComputeFamilyButton({
  name,
  description,
  selected,
  onSelect,
  isCPU = false,
}: ComputeFamilyButtonProps) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      tabIndex={selected ? 0 : -1}
      onClick={onSelect}
      className={classNames(
        "group relative min-w-[145px] flex-1 rounded-lg border px-3 py-2.5 text-left transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-highlight lg:w-full lg:flex-none",
        selected
          ? "border-highlight bg-highlight/10"
          : "border-zinc-800 bg-zinc-900/60 hover:border-zinc-600 hover:bg-zinc-900",
      )}
    >
      <div className="flex items-start gap-3">
        <div
          className={classNames(
            "mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md border",
            selected
              ? "border-highlight/50 bg-highlight/10"
              : "border-zinc-700 bg-black",
          )}
        >
          {isCPU ? (
            <CpuChipIcon className="h-4 w-4 text-secondary" />
          ) : (
            <img src="/logos/svg/nvidia-logo.svg" alt="" className="h-4 w-6" />
          )}
        </div>
        <span className="min-w-0 flex-1">
          <span className="flex items-center justify-between gap-2">
            <span className="truncate text-sm font-semibold text-white">
              {name}
            </span>
            {selected && (
              <CheckIcon className="h-4 w-4 shrink-0 text-highlight" />
            )}
          </span>
          <span className="mt-1 block text-xs text-secondary">
            {description}
          </span>
        </span>
      </div>
    </button>
  );
}

interface GPUVariantChoiceProps {
  gpuName: string;
  variant: GPUVariant;
  selected: boolean;
  selectedGpuCount: number | undefined;
  onSelectGpuCount: (gpuCount: number) => void;
  pickerAllowedLocations?: Set<string> | null;
}

const GPUVariantChoice: React.FC<GPUVariantChoiceProps> = ({
  gpuName,
  variant,
  selected,
  selectedGpuCount,
  onSelectGpuCount,
  pickerAllowedLocations,
}) => {
  const pricedInstances = variant.instances
    .map((instance) =>
      Number(getFilteredBasePriceAmount(instance, pickerAllowedLocations)),
    )
    .filter((price) => Number.isFinite(price) && price > 0);
  const lowestPrice =
    pricedInstances.length > 0 ? Math.min(...pricedInstances) : undefined;
  const optionCount = variant.instances.length;
  const compactMemoryLabel = variant.memoryLabel
    .replace(/(\.\d*?[1-9])0+(?=\s)/, "$1")
    .replace(/\.0+(?=\s)/, "");

  return (
    <div
      className={classNames(
        "flex min-h-[150px] flex-col rounded-md border p-4 text-left transition-colors duration-150 ease-[cubic-bezier(0.16,1,0.3,1)]",
        selected
          ? "border-highlight bg-highlight/10"
          : "border-primaryBorder bg-baseBg",
      )}
    >
      <span className="text-xl font-semibold text-primary">{gpuName}</span>
      <span className="mt-1 text-sm text-secondary">
        {compactMemoryLabel} VRAM / GPU
      </span>
      <fieldset className="mt-3 pb-3">
        <legend className="text-xs text-secondary">GPU count</legend>
        <div className="mt-1.5 flex flex-wrap gap-2">
          {variant.gpuCounts.map((gpuCount) => {
            const countSelected = selected && selectedGpuCount === gpuCount;

            return (
              <button
                key={gpuCount}
                type="button"
                aria-pressed={countSelected}
                aria-label={`${gpuCount} ${gpuName} ${gpuCount === 1 ? "GPU" : "GPUs"} with ${compactMemoryLabel} VRAM per GPU`}
                onClick={() => onSelectGpuCount(gpuCount)}
                className={classNames(
                  "rounded-md border px-2.5 py-1 text-sm font-medium transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-highlight",
                  countSelected
                    ? "border-highlight bg-highlight/20 text-highlight"
                    : "border-primaryBorder bg-secondaryBg text-secondary hover:border-borderPrimary hover:text-primary",
                )}
              >
                {gpuCount}×
              </button>
            );
          })}
        </div>
      </fieldset>
      <span className="mt-auto flex items-end justify-between gap-3 border-t border-primaryBorder pt-3">
        <span className="text-xs text-secondary">
          {optionCount} {optionCount === 1 ? "option" : "options"}
        </span>
        {lowestPrice !== undefined && (
          <span className="text-sm font-semibold text-primary">
            From ${withServiceCharge(lowestPrice).toFixed(2)}/hr
          </span>
        )}
      </span>
    </div>
  );
};

interface ComputeOptionsProps {
  selectedGpu: string;
  selectedVariantKey: string | undefined;
  selectedGpuCount: number | undefined;
  setSelectedVariantKey(
    variantKey: string | undefined,
    gpuCount?: number,
  ): void;
  displayGPUInstances: GPUInstanceType[];
  setSelectedInstance(instanceObj: GPUInstanceType | undefined): void;
  flexiblePortControlOnly: boolean;
  flexibleStorageOnly: boolean;
  pickerAllowedLocations?: Set<string> | null;
}

const ComputeOptions: React.FC<ComputeOptionsProps> = ({
  displayGPUInstances,
  setSelectedInstance,
  selectedGpu,
  selectedVariantKey,
  selectedGpuCount,
  setSelectedVariantKey,
  flexiblePortControlOnly,
  flexibleStorageOnly,
  pickerAllowedLocations,
}) => {
  const [sortBy, setSortBy] = useState<string>("price");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  const { isEntitled } = useFreeTier();

  const variants = useMemo(
    () => groupInstancesByGPUVariant(displayGPUInstances),
    [displayGPUInstances],
  );

  const selectedVariant =
    variants.find((variant) => variant.key === selectedVariantKey) ??
    variants[0];
  const activeGpuCount =
    selectedVariantKey === selectedVariant?.key &&
    selectedGpuCount !== undefined &&
    selectedVariant?.gpuCounts.includes(selectedGpuCount)
      ? selectedGpuCount
      : selectedVariant?.gpuCounts[0];

  const selectedInstances = useMemo(
    () =>
      selectedVariant?.instances.filter(
        (instance) => instance.supported_gpus?.[0]?.count === activeGpuCount,
      ) ?? [],
    [activeGpuCount, selectedVariant],
  );

  useEffect(() => {
    if (
      selectedVariantKey &&
      !variants.some((variant) => variant.key === selectedVariantKey)
    ) {
      setSelectedVariantKey(undefined);
    }
  }, [selectedVariantKey, setSelectedVariantKey, variants]);
  const sortedInstances = useMemo(() => {
    const instances = [...selectedInstances];

    instances.sort((a, b) => {
      let aValue: number = 0;
      let bValue: number = 0;

      switch (sortBy) {
        case "price":
          aValue = Number(
            getFilteredBasePriceAmount(a, pickerAllowedLocations) || 0,
          );
          bValue = Number(
            getFilteredBasePriceAmount(b, pickerAllowedLocations) || 0,
          );
          break;
        case "vram":
          aValue = parseMemoryToGiB(a.supported_gpus?.[0]?.memory || "0");
          bValue = parseMemoryToGiB(b.supported_gpus?.[0]?.memory || "0");
          break;
        case "ram":
          aValue = parseMemoryToGiB(a.memory);
          bValue = parseMemoryToGiB(b.memory);
          break;
        case "cpu":
          aValue = Number(a.vcpu || 0);
          bValue = Number(b.vcpu || 0);
          break;
      }

      // Handle sort direction
      if (sortDirection === "asc") {
        return aValue - bValue;
      } else {
        return bValue - aValue;
      }
    });

    return instances;
  }, [selectedInstances, sortBy, sortDirection, pickerAllowedLocations]);

  return (
    <div className="flex flex-col gap-6">
      <section aria-labelledby="gpu-variant-heading">
        <SectionHeading id="gpu-variant-heading" title="Configuration" />
        <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {variants.map((variant) => (
            <GPUVariantChoice
              key={variant.key}
              gpuName={selectedGpu}
              variant={variant}
              selected={selectedVariant?.key === variant.key}
              selectedGpuCount={activeGpuCount}
              onSelectGpuCount={(gpuCount) =>
                setSelectedVariantKey(variant.key, gpuCount)
              }
              pickerAllowedLocations={pickerAllowedLocations}
            />
          ))}
        </div>
      </section>

      {selectedInstances.length > 0 && (
        <section
          aria-label="Available compute options"
          className="border-t border-primaryBorder pt-6"
        >
          <div className="flex justify-end">
            <div className="flex flex-row gap-3 items-center">
              <SortByDropdown
                sortBy={sortBy}
                setSortBy={setSortBy}
                hasMultipleVramOptions={false}
              />
              <button
                onClick={() =>
                  setSortDirection(sortDirection === "asc" ? "desc" : "asc")
                }
                className="flex items-center gap-2 px-3 py-2 rounded-md border border-zinc-700 bg-zinc-800 hover:bg-zinc-700 text-white transition-colors"
                title={sortDirection === "asc" ? "Ascending" : "Descending"}
              >
                {sortDirection === "asc" ? (
                  <ArrowUpIcon className="w-4 h-4" />
                ) : (
                  <ArrowDownIcon className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>

          <div className="mt-4 flex flex-col gap-3">
            {sortedInstances.map((instance) => (
              <RackCard
                key={instance.type}
                instanceType={instance}
                setInstanceType={setSelectedInstance}
                gpuCount={instance.supported_gpus[0].count}
                vramSize={instance.supported_gpus[0].memory}
                ramSize={String(instance.memory)}
                cpuCount={instance.vcpu}
                cloudProvider={displayPlatformType(
                  GetProviderName(instance) || "unknown",
                )}
                cloud={GetCloudName(instance)}
                price={withServiceCharge(
                  getFilteredBasePriceAmount(instance, pickerAllowedLocations),
                ).toFixed(2)}
                isFreeTier={
                  isEntitled &&
                  isFreeTierInstance(
                    getFilteredBasePriceAmount(
                      instance,
                      pickerAllowedLocations,
                    ),
                  )
                }
                flexiblePortControlOnly={flexiblePortControlOnly}
                flexibleStorageOnly={flexibleStorageOnly}
              />
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

interface DgxComputeOptionsProps {
  setSelectedInstance(instanceObj: GPUInstanceType | undefined): void;
  pickerAllowedLocations?: Set<string> | null;
}

const DGXSetup: React.FC<DgxComputeOptionsProps> = ({
  setSelectedInstance,
  pickerAllowedLocations,
}) => {
  const orgContext = useContext(OrgContext);
  const instanceTypesContext = useContext(InstanceTypesContext);
  const [showNext, setShowNext] = useState(false);
  const dgxFilteredInstances =
    instanceTypesContext.GPUInstancesAvailable.filter((filteredInstances) => {
      return (
        getPrimaryInstanceCloudCred(filteredInstances)?.id === "dgxc-gcp-1"
      );
    });
  const [text] = useWindupString("No DGX Instances Available");

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowNext(true);
    }, 2000);
    // Cleanup function
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="flex flex-col bg-black h-[380px] p-4 rounded-md relative">
      <div className="flex flex-row">
        <div className="flex flex-col justify-center items-center w-fit">
          <div className="flex flex-col w-full items-center">
            <div
              className="flex flex-row items-center"
              style={{
                color: "hsl(46.67deg 90% 68.63%)",
              }}
            >
              <svg
                width="24"
                height="16"
                viewBox="0 0 249 165"
                className="mr-1"
                fill="currentColor"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M25.43 70.902C25.43 70.902 47.934 37.699 92.867 34.264V22.218C43.098 26.215 0 68.367 0 68.367C0 68.367 24.41 138.932 92.867 145.393V132.589C42.63 126.269 25.43 70.902 25.43 70.902ZM92.867 107.125V118.851C54.899 112.082 44.36 72.614 44.36 72.614C44.36 72.614 62.59 52.419 92.867 49.144V62.011C92.844 62.011 92.828 62.004 92.809 62.004C76.918 60.097 64.504 74.942 64.504 74.942C64.504 74.942 71.462 99.933 92.867 107.125ZM92.867 0V22.218C94.328 22.106 95.789 22.011 97.258 21.961C153.84 20.054 190.707 68.367 190.707 68.367C190.707 68.367 148.364 119.855 104.25 119.855C100.207 119.855 96.422 119.48 92.867 118.85V132.589C95.907 132.975 99.059 133.202 102.348 133.202C143.399 133.202 173.086 112.237 201.832 87.424C206.598 91.241 226.11 100.527 230.121 104.592C202.789 127.475 139.09 145.921 102.977 145.921C99.496 145.921 96.153 145.71 92.867 145.393V164.699H248.899V0H92.867ZM92.867 49.144V34.264C94.313 34.163 95.77 34.086 97.258 34.039C137.946 32.761 164.64 69.004 164.64 69.004C164.64 69.004 135.808 109.047 104.894 109.047C100.445 109.047 96.457 108.332 92.867 107.125V62.011C108.707 63.925 111.894 70.922 121.417 86.797L142.597 68.938C142.597 68.938 127.136 48.661 101.073 48.661C98.24 48.66 95.529 48.859 92.867 49.144Z" />
              </svg>
              <span className="text-2xl font-bold">NVIDIA DGX Cloud</span>
            </div>
          </div>
          <div className="mt-5">
            <img
              src={NvidiaDGXDataCenter}
              alt="NVIDIA NIM Influencer Banner"
              className="h-[300px] w-[500px]"
            />
          </div>
        </div>
        <div className="flex flex-col w-full space-y-6 ml-5 justify-center">
          {dgxFilteredInstances.length === 0 && (
            <div className="flex relative">
              <div className="flex flex-col items-center absolute top-0 w-full">
                <span className="text-xl font-bold text-slate-400">{text}</span>
              </div>
              <DGXComputeCard
                onClick={() => {}}
                disabled={true}
                instance={dummyDGXInstanceObject}
              />
            </div>
          )}
          {dgxFilteredInstances.map((instance) => (
            <>
              <DGXComputeCard
                onClick={() => {
                  setSelectedInstance(instance);
                }}
                disabled={false}
                instance={instance}
                pickerAllowedLocations={pickerAllowedLocations}
              />
              {showNext && (
                <div className="absolute bottom-8 right-8 pulse-ready-button">
                  <Button
                    label={"Next"}
                    style={{
                      backgroundColor: "hsl(46.67deg 90% 68.63%)",
                      color: "#000",
                    }}
                    onClick={() => {
                      setSelectedInstance(instance);
                    }}
                  />
                </div>
              )}
            </>
          ))}
        </div>
      </div>
    </div>
  );
};

interface EmptyStateProps {
  selectedGpuCount: string;
  selectedVram: string;
  onResetFilters: () => void;
}

const EmptyState: React.FC<EmptyStateProps> = ({
  selectedGpuCount,
  selectedVram,
  onResetFilters,
}) => {
  const hasActiveFilters = selectedGpuCount !== "All" || selectedVram !== "All";

  return (
    <div className="flex flex-col items-center justify-center py-16 px-4">
      <div className="flex flex-col items-center max-w-md text-center">
        {/* Icon */}
        <div className="mb-4 p-4 rounded-full bg-zinc-800/50">
          <FunnelIcon className="w-12 h-12 text-secondary" />
        </div>

        {/* Heading */}
        <h3 className="text-lg font-semibold text-white mb-2">
          No instances found
        </h3>

        {/* Message */}
        <p className="text-sm text-gray-400 mb-6">
          {hasActiveFilters ? (
            <>
              No GPU instances match your current filter criteria. Try adjusting
              or removing some filters to see more options.
            </>
          ) : (
            <>
              No GPU instances match your current filter criteria. Try adjusting
              or removing some filters to see more options.
            </>
          )}
        </p>

        {/* Active Filters Summary */}
        {hasActiveFilters && (
          <div className="mb-6 p-3 rounded-md bg-zinc-800/50 border border-zinc-700 w-full">
            <p className="text-xs font-medium text-gray-300 mb-2">
              Active filters:
            </p>
            <div className="flex flex-wrap gap-2 justify-center">
              {selectedGpuCount !== "All" && (
                <span className="inline-flex items-center px-2 py-1 rounded-md bg-highlight/20 text-highlight text-xs">
                  GPU Count: {selectedGpuCount}x
                </span>
              )}
              {selectedVram !== "All" && (
                <span className="inline-flex items-center px-2 py-1 rounded-md bg-highlight/20 text-highlight text-xs">
                  VRAM: {selectedVram}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Reset Button */}
        {hasActiveFilters && (
          <button
            onClick={onResetFilters}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-highlight hover:bg-highlightDarker text-black font-medium text-sm transition-colors duration-200"
          >
            <ArrowPathIcon className="w-4 h-4" />
            Reset Filters
          </button>
        )}
      </div>
    </div>
  );
};

interface CPUOptionsProps {
  cpuInstances: GPUInstanceType[];
  flexiblePortControlOnly: boolean;
  flexibleStorageOnly: boolean;
  setSelectedInstance(instanceObj: GPUInstanceType | undefined): void;
  pickerAllowedLocations?: Set<string> | null;
}

const CPUOptions: React.FC<CPUOptionsProps> = ({
  cpuInstances,
  flexiblePortControlOnly,
  flexibleStorageOnly,
  setSelectedInstance,
  pickerAllowedLocations,
}) => {
  const { isEntitled } = useFreeTier();
  const sortedInstances = useMemo(() => {
    return [...cpuInstances].sort(
      (a, b) =>
        Number(getFilteredBasePriceAmount(a, pickerAllowedLocations) || 0) -
        Number(getFilteredBasePriceAmount(b, pickerAllowedLocations) || 0),
    );
  }, [cpuInstances, pickerAllowedLocations]);

  return (
    <>
      <h2 className="text-lg font-medium leading-6 text-gray-900 dark:text-white my-3">
        Select a <span className="text-highlight">CPU</span> instance type
      </h2>
      <div className="flex flex-col gap-3">
        {sortedInstances.map((instance) => (
          <CpuInstanceCard
            key={instance.type}
            instance={instance}
            selected={false}
            onSelect={() => setSelectedInstance(instance)}
            isFreeTier={
              isEntitled &&
              isFreeTierInstance(
                getFilteredBasePriceAmount(instance, pickerAllowedLocations),
              )
            }
            flexiblePortControlOnly={flexiblePortControlOnly}
            flexibleStorageOnly={flexibleStorageOnly}
            pickerAllowedLocations={pickerAllowedLocations}
          />
        ))}
      </div>
    </>
  );
};
